import type { ReactNode } from 'react';
import { PersonaAiBadge } from '../PersonaAiBadge';
import { isAiGeneratedPersona } from '../../lib/personaSource';
import type { Participant, SessionInfo, SessionUsage } from './types';
import {
  formatDurationSeconds,
  formatSessionDateTime,
  getSessionStartIso,
} from './sessionDisplayUtils';

export function PracticeSessionReviewHeader({
  sessionInfo,
  sessionUsage,
  participants,
  isOwnSession,
  modeLabel,
  showCost,
  children,
}: {
  sessionInfo: SessionInfo;
  sessionUsage?: SessionUsage | null;
  participants: Participant[];
  isOwnSession: boolean;
  modeLabel: string;
  showCost: boolean;
  /** Extra controls (e.g. Back, transcript toggle) rendered in the top-right row. */
  children?: ReactNode;
}) {
  const startIso = getSessionStartIso(sessionInfo);
  const startedDisplay = formatSessionDateTime(startIso);
  const durationDisplay = formatDurationSeconds(sessionInfo.durationSeconds ?? null);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 md:mb-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">
            {sessionInfo.personas && sessionInfo.personas.length > 1
              ? `Committee (${sessionInfo.personas.length})`
              : sessionInfo.personaName || 'Practice Session'}
          </h1>
          {isAiGeneratedPersona(sessionInfo.personaSource) && <PersonaAiBadge />}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200">
            {modeLabel}
          </span>
          {sessionInfo.personas && sessionInfo.personas.length > 1 && (
            <span className="text-xs sm:text-sm text-gray-500 truncate">
              {sessionInfo.personas.map((p) => p.name).join(', ')}
            </span>
          )}
        </div>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          {!isOwnSession && (
            <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              {sessionInfo.userName || sessionInfo.userEmail}
            </span>
          )}
          {participants.length > 1 && (
            <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              {participants.length} participants
            </span>
          )}
          {sessionInfo.pitchStyle ? (
            <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {sessionInfo.pitchStyle}
            </span>
          ) : null}
          {sessionInfo.goalLabel && (
            <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Goal: {sessionInfo.goalLabel}
            </span>
          )}
        </p>
        <p className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {startedDisplay && <span>Started: {startedDisplay}</span>}
          {durationDisplay && <span>Duration: {durationDisplay}</span>}
        </p>
        {sessionUsage && (sessionUsage.inputTokens > 0 || sessionUsage.outputTokens > 0) && (
          <p className="text-xs text-gray-500 mt-1">
            AI usage: {sessionUsage.inputTokens + sessionUsage.outputTokens} tokens
            {sessionUsage.callCount > 0 && ` · ${sessionUsage.callCount} calls`}
            {showCost && sessionUsage.costUsd !== undefined && (
              <span> · Est. ${sessionUsage.costUsd.toFixed(2)} USD</span>
            )}
          </p>
        )}
      </div>
      {children ? (
        <div className="flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap justify-end">
          {children}
        </div>
      ) : null}
    </div>
  );
}
